/**
 * Что можно сделать с самой плашкой чата в списке — помимо «открыть».
 *
 * <p>Плашка в списке до сих пор понимала ровно одно касание: открыть чат. Всё остальное
 * пряталось внутри — позвонить можно было только из шапки открытого чата, отписаться от
 * канала — только из его настроек, а выйти из группы — только из карточки группы. То есть
 * действия, которые в списке напрашиваются первыми, требовали сначала войти туда, откуда
 * потом придётся выйти.
 *
 * <p>Правила «кому что доступно» живут здесь, а не в разметке, по той же причине, что и
 * {@link ./chatCapabilities}: тип чата и роль в нём — это условия, которые проще проверить
 * тестом, чем удержанием плашки на устройстве. В `ChatListScreen` остаётся жест и лист
 * действий, а решение «показывать ли их вообще» приходит отсюда.
 */

import type { ChatSummary } from '../../api/types';

/** Минимум полей сводки, который нужен правилам. Тесты не собирают весь `ChatSummary`. */
type ChatTile = Pick<ChatSummary, 'type'> &
  Partial<Pick<ChatSummary, 'peerUserId' | 'myRole'>>;

/**
 * Звонок с плашки — только личная переписка.
 *
 * <p>Группа и канал отпадают не «пока не сделано», а по устройству звонка на этом экране:
 * `callScope: 'direct'` адресуется собеседнику, и без `peerUserId` звать некого. «Избранное»
 * — переписка с самим собой, звонить туда некому вовсе (см. `chatCapabilities`).
 */
export function chatTileCallPeerId(chat: ChatTile): string | null {
  if (chat.type !== 'DIRECT') return null;
  const peer = chat.peerUserId;
  return typeof peer === 'string' && peer.length > 0 ? peer : null;
}

/** Что означает «уйти отсюда» для этой плашки. */
type ChatTileLeaveKind = 'channelUnsubscribe' | 'groupLeave';

/**
 * Свайп плашки влево — выход из группы или отписка от канала. И только для тех, кто
 * подписчик, а не владелец: у администратора «уйти» означает удалить чат у всех, и такое
 * решение не принимают смахиванием пальца мимо списка (та же развилка в
 * `ChannelSettingsModal`: `canUnsubscribe={myRole !== 'ADMIN'}`).
 *
 * <p>Личные чаты и «Избранное» не свайпаются: уйти из переписки с человеком нельзя, её
 * можно только удалить — и для этого на плашке есть отдельная кнопка со своим вопросом.
 */
export function chatTileLeaveKind(chat: ChatTile): ChatTileLeaveKind | null {
  if (chat.myRole === 'ADMIN') return null;
  if (chat.type === 'CHANNEL') return 'channelUnsubscribe';
  if (chat.type === 'GROUP') return 'groupLeave';
  return null;
}
